import { formatAddress, formatDate, titleCase } from '../BusinessDetailReport/businessReportModel'
import { moreFromLexis } from './moreFromLexis'

const list = (value) => (Array.isArray(value) ? value : value ? [value] : [])
const text = (value) => (value == null || typeof value === 'object' ? '' : String(value)).trim()
const upper = (value) => text(value).toUpperCase()
const dateValue = (date) => (date && typeof date === 'object' && date.Year ? new Date(date.Year, (date.Month || 1) - 1, date.Day || 1).getTime() : 0)
const nameCase = (value) => titleCase(value).replace(/(^|\s)([a-z])(?=\s|$)/g, (m, space, letter) => space + letter.toUpperCase())
const amount = (value) => Number(text(value).replace(/[^0-9.]/g, '')) || 0
const money = (value) => (amount(value) > 0 ? `$${Math.round(amount(value)).toLocaleString('en-US')}` : '')
const records = (section) => {
    const value = section && typeof section === 'object' ? Object.values(section).find(Array.isArray) : null
    return (value || []).filter(r => r && typeof r === 'object')
}
const YEARS = 365.25 * 86400000

export function buildProperty(person, now = new Date()) {
    const parcels = []
    const agrees = (x, y) => !x || !y || x === y
    list(person?.Properties?.Property).filter(p => p && typeof p === 'object').forEach((p, index) => {
        const entities = list(p.Entities?.Entity).filter(e => e && typeof e === 'object')
        const site = entities.find(e => /PROPERTY/.test(upper(e.EntityType)) || upper(e.EntityTypeCode) === 'P')
        const a = p.Assessment || {}
        const deed = p.Deed || {}
        const state = upper(a.StateCode || site?.Address?.State)
        const county = upper(a.County || deed.County)
        const fips = upper(a.FipsCode)
        const parcelId = text(a.ParcelId || deed.ParcelId)
        const key = parcelId || formatAddress(site?.Address) || text(p.SourcePropertyRecordId) || `record ${index}`
        let entry = parcels.find(e => e.key === key && (!parcelId || (agrees(e.state, state)
            && (e.fips && fips ? e.fips === fips : agrees(e.county, county)))))
        if (!entry) {
            entry = { key, assessments: [], deeds: [], owners: new Set(), address: '', parcelId: '', state, county, fips, records: [] }
            parcels.push(entry)
        }
        entry.state = entry.state || state
        entry.county = entry.county || county
        entry.fips = entry.fips || fips
        entry.parcelId = entry.parcelId || text(a.ParcelId || deed.ParcelId)
        entry.records.push(p)
        if (p.Assessment) entry.assessments.push(a)
        if (p.Deed) entry.deeds.push(deed)
        entry.address = entry.address || formatAddress(site?.Address)
        entities.filter(e => e !== site).flatMap(e => list(e.Names?.Name)).filter(n => n && typeof n === 'object')
            .map(n => nameCase(n.CompanyName || [n.First, n.Middle, n.Last].filter(Boolean).join(' '))).filter(Boolean)
            .forEach(n => entry.owners.add(n))
    })
    const properties = parcels.map(e => {
        const parcel = e.parcelId
        const where = { state: e.state, county: e.county }
        const a = [...e.assessments].sort((x, y) => Number(y.TaxYear || 0) - Number(x.TaxYear || 0))[0] || {}
        const deed = [...e.deeds].sort((x, y) => dateValue(y.ContractDate || y.RecordingDate) - dateValue(x.ContractDate || x.RecordingDate))[0] || {}
        const value = amount(a.MarketTotalValue) || amount(a.AssessedTotalValue)
        const sale = a.SaleDate || deed.ContractDate
        const price = a.SaleDate ? amount(a.SalesPrice) : amount(deed.SalesPrice)
        return {
            more: moreFromLexis(e.records, 'Properties/Property[]'),
            parcel, where, value, latest: Math.max(dateValue(sale), dateValue(a.RecordingDate), Number(a.TaxYear || 0) ? new Date(Number(a.TaxYear), 0, 1).getTime() : 0),
            title: e.address || (parcel ? `Parcel ${parcel}` : 'Property'),
            facts: [titleCase(a.LandUse || deed.PropertyUseDesc), value && `${amount(a.MarketTotalValue) ? 'Market value' : 'Assessed value'} ${money(value)}${a.TaxYear ? ` (${a.TaxYear})` : ''}`,
                sale && `Bought ${formatDate(sale)}${price ? ` for ${money(price)}` : ''}`].filter(Boolean).join(' · '),
            chips: [upper(a.OwnerOccupied) === 'Y' && { label: 'Owner occupied', tone: 'neutral' }].filter(Boolean),
            details: [
                ['Owners on record', [...e.owners].join('; ')],
                ['Market value', [money(a.MarketTotalValue), a.MarketValueYear && `(${a.MarketValueYear})`].filter(Boolean).join(' ')],
                ['Assessed value', [money(a.AssessedTotalValue), a.AssessedValueYear && `(${a.AssessedValueYear})`].filter(Boolean).join(' ')],
                ['Property tax', [money(a.TaxAmount), a.TaxYear && `(${a.TaxYear})`].filter(Boolean).join(' ')],
                ['Bought', [formatDate(sale), price && `for ${money(price)}`, titleCase(deed.DeedSourcePropertyRecord?.TransactionTypeDesc)].filter(Boolean).join(' · ')],
                ['Mortgage', [titleCase(a.MortgageLender || deed.LenderInfo?.LenderName), money(a.MortgageLoanAmount || deed.FirstTdLoanAmount),
                    titleCase(a.MortgageLoanType || deed.FirstTdLoanType)].filter(Boolean).join(' · ')],
                ['Built', [text(a.YearBuilt), amount(a.SourcePropertyRecord?.LivingSquareFeet) && `${amount(a.SourcePropertyRecord.LivingSquareFeet).toLocaleString('en-US')} sq ft`]
                    .filter(Boolean).join(' · ')],
                ['Homestead exemption', upper(a.HomesteadHomeownerExemption) === 'Y' ? 'Yes' : ''],
                ['County', titleCase(a.County || deed.County)],
                ['Parcel', parcel],
            ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        }
    }).sort((x, y) => y.latest - x.latest)

    const distress = [
        ...records(person?.Foreclosures).map(f => ({ ...f, kind: 'foreclosure' })),
        ...records(person?.NoticesOfDefaults).map(f => ({ ...f, kind: 'notice' })),
    ].map(f => {
        const type = upper(f.DocumentType || f.DeedType)
        const release = /RELEASE|CANCEL|RESCI/.test(type)
        const recorded = f.RecordingDate || f.FilingDate
        const years = dateValue(recorded) ? (now.getTime() - dateValue(recorded)) / YEARS : null
        const plaintiffs = list(f.Plaintiffs?.Plaintiff).map(p => (typeof p === 'string' ? p : p?.Name || p?.CompanyName)).map(titleCase).filter(Boolean)
        return {
            more: moreFromLexis(f, f.kind === 'foreclosure' ? 'Foreclosures/Foreclosure[]' : 'NoticesOfDefaults/NoticeOfDefaults[]'),
            recordedAt: dateValue(recorded), release, parcel: text(f.ParcelNumber),
            ids: [f.CaseNumber, f.LoanNumber].map(upper).filter(Boolean),
            where: { state: upper(f.SiteAddress?.State), county: upper(f.County || f.SiteAddress?.County) },
            title: titleCase(type) || (f.kind === 'foreclosure' ? 'Foreclosure' : 'Notice of default'),
            facts: [recorded && `Recorded ${formatDate(recorded)}`, formatAddress(f.SiteAddress), plaintiffs[0] && `by ${plaintiffs[0]}`].filter(Boolean).join(' · '),
            chips: [release ? { label: 'Released', tone: 'neutral' }
                : { label: years != null && years <= 7 ? 'Recent' : f.kind === 'foreclosure' ? 'Foreclosure' : 'Default notice', tone: years != null && years <= 7 ? 'declined' : 'watch' }],
            details: [
                ['Plaintiff or lender', [...plaintiffs, titleCase(f.Lender?.Name || f.Lender?.CompanyName || (typeof f.Lender === 'string' ? f.Lender : ''))].filter(Boolean).join('; ')],
                ['Loan in default', [money(f.AmountOfLoanDefault || f.LoanAmount), formatDate(f.DateOfLoanDefault)].filter(Boolean).join(' · ')],
                ['Final judgment', money(f.FinalJudgmentAmount)],
                ['Auction', [formatDate(f.AuctionDate), text(f.AuctionTime), formatAddress(f.AuctionLocation)].filter(Boolean).join(' · ')],
                ['Loan type', titleCase(f.LoanTypeDescription || f.LoanType)],
                ['Case number', text(f.CaseNumber)],
                ['Document', [text(f.DocumentNumber), f.DocumentYear && `(${f.DocumentYear})`].filter(Boolean).join(' ')],
                ['Parcel', text(f.ParcelNumber)],
            ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        }
    }).sort((x, y) => (x.release - y.release) || y.recordedAt - x.recordedAt)

    const sameArea = (x, y) => !x || !y || x === y
    distress.filter(d => d.release && d.parcel).forEach(rel => {
        const candidates = distress.filter(d => !d.release && !d.releasedBy && d.parcel === rel.parcel && sameArea(d.where.state, rel.where.state)
            && sameArea(d.where.county, rel.where.county) && (!rel.recordedAt || !d.recordedAt || d.recordedAt <= rel.recordedAt))
        const released = rel.ids.length ? candidates.find(d => d.ids.some(id => rel.ids.includes(id)))
            : candidates.length === 1 ? candidates[0] : null
        if (!released) return
        released.releasedBy = rel
        released.chips = [{ label: `Released${rel.recordedAt ? ` ${new Date(rel.recordedAt).getFullYear()}` : ''}`, tone: 'neutral' }]
    })
    distress.sort((x, y) => (Boolean(x.release || x.releasedBy) - Boolean(y.release || y.releasedBy)) || y.recordedAt - x.recordedAt)
    const open = distress.filter(d => !d.release && !d.releasedBy)
    properties.forEach(prop => {
        const hit = open.find(d => d.parcel && d.parcel === prop.parcel && sameArea(d.where.state, prop.where.state) && sameArea(d.where.county, prop.where.county))
        if (hit) prop.chips.unshift({ label: hit.recordedAt ? `${hit.title} ${new Date(hit.recordedAt).getFullYear()}` : hit.title, tone: hit.chips[0].tone })
    })
    return {
        properties, distress,
        summary: {
            properties: properties.length,
            value: properties.reduce((sum, p) => sum + p.value, 0),
            distress: open.length,
            recent: open.filter(d => d.chips[0].tone === 'declined').length,
        },
        hasData: properties.length + distress.length > 0,
    }
}
